import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from "../../../Images/ArchiveRestore.svg";
import { ReactComponent as Back } from '../../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../../Images/FilterIcon.svg';
import { fetchArchivedInquiryList, unArchiveInquiry, resetArchiveData } from '../../../slices/inquiry';

function ArchiveInquiryList() {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagesize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState('');

    const dispatch = useDispatch();
    const { archiveData = [], totalRecord = 0, loading } = useSelector(state => state.inquiry || {});
    useEffect(() => {
        dispatch(resetArchiveData());
        dispatch(fetchArchivedInquiryList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
    }, [dispatch, page, pagesize, search, orderBy]);

    const handlePageSizeChange = (current, size) => {
        setPage(1); // Reset to first page if needed
        setPageSize(size); // You must have a setter or state update
    };

    const handleSearch = e => {
        setSearch(e.target.value);
        setPage(1); // Reset to the first page when searching
    };

    const resetFilter = () => {
        setSearch('');
        setPage(1);
        setOrderBy('');
    };

    const handlePageChange = newPage => {
        setPage(newPage);
    };
    const GetSortingEnumValue = (sortField, sortOrder) => {
        let sortingEnumKey = "id";

        if (sortField === "customer_name" && sortOrder === "ascend") {
            sortingEnumKey = "customer_name";
        }
        if (sortField === "customer_name" && sortOrder === "descend") {
            sortingEnumKey = "-customer_name";
        }
        if (sortField === "phone_number" && sortOrder === "ascend") {
            sortingEnumKey = "phone_number";
        }
        if (sortField === "phone_number" && sortOrder === "descend") {
            sortingEnumKey = "-phone_number";
        }


        return sortingEnumKey;
    };
    const handleTableChange = async (pagination, filters, sorter, ordering) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
            setOrderBy(orderBy);
        }
        setPageSize(pagination.pageSize);
    };
    const dataSource = archiveData?.map((item, index) => ({
        ...item,
        key: item.id || index,
        SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
    }));


    const handleRestore = (id) => {
        if (!id) {
            message.error('Missing ID for deletion!');
            return;
        }

        dispatch(unArchiveInquiry(id))
            .unwrap()
            .then(() => {
                message.success('Customer Unarchived successfully!');
                dispatch(fetchArchivedInquiryList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
            })
            .catch((error) => {
                if (typeof error === 'object' && error !== null) {
                    const messages = [];
                    for (const key in error) {
                        if (Array.isArray(error[key])) {
                            messages.push(`${key}: ${error[key].join(', ')}`);
                        }
                    }
                    if (messages.length > 0) {
                        message.error(messages.join('; '));
                    } else {
                        message.error('An unknown error occurred.');
                    }
                } else if (typeof error === 'string') {
                    message.error(error); // Handle string error
                } else if (error instanceof Error) {
                    message.error(error.message); // Handle Error object
                } else {
                    message.error('An unknown error occurred.');
                }
            });
    };

    const columns = [
        {
            title: 'Sr. No.',
            dataIndex: 'SrNo',
            key: 'SrNo',
            width: 60,
            render: (text, record, index) => {
                return (page - 1) * pagesize + index + 1;
            },
        },
        {
            title: 'Inquiry ID',
            dataIndex: 'inquiry_number',
            key: 'inquiry_number',
            sorter: true
        },
        {
            title: 'Date',
            dataIndex: 'inquiry_date',
            key: 'inquiry_date',
            sorter: true,
            render: record => {
                return (
                    <span>
                        {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
                    </span>
                );
            },
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            sorter: true,
        },
        {
            title: 'Contact Person',
            dataIndex: 'contact_persons',
            key: 'contact_person',
            render: (contact_persons) => {
                if (contact_persons && contact_persons.length > 0) {
                    return contact_persons[0].name || 'N/A';
                }
                return 'N/A';
            },
        },
        {
            title: 'Email',
            dataIndex: 'contact_persons',
            key: 'email',
            render: (contact_persons) => {
                if (contact_persons && contact_persons.length > 0) {
                    return contact_persons[0].email || 'N/A';
                }
                return 'N/A';
            },
        },
        {
            title: 'Mobile',
            dataIndex: 'contact_persons',
            key: 'mobile',
            render: (contact_persons) => {
                if (contact_persons && contact_persons.length > 0) {
                    return contact_persons[0].mobile || 'N/A';
                }
                return 'N/A';
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true
        },
        {
            title: 'Assigned To',
            dataIndex: 'assigned_user',
            key: 'assigned_user',
            render: assigned_user => {
                return <span>{assigned_user?.name ? assigned_user.name : 'Not Assigned'}</span>;
            },
        },
        {
            title: 'Archive At',
            dataIndex: 'archive_at',
            key: 'archive_at',
            sorter: true,
            render: (record) => {
                return (
                    <span>{dayjs(record).format('DD-MM-YYYY HH:mm')} </span>
                )
            }
        },
        {
            title: 'Archive By',
            dataIndex: 'deleted_by',
            key: 'deleted_by',
            sorter: true,
            render: (record) => {
                return (
                    <span>{record?.first_name ? `${record.first_name}` : 'N/A'} </span>
                )
            }

        },
        {
            title: 'Action',
            dataIndex: 'action',
            fixed: 'right',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Tooltip placement="top" title="Restore">
                        <Popconfirm
                            title="Are you sure you want to Unarchive this record?"
                            onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
                            onCancel={() => message.info('Action cancelled')} // Optional cancel action
                            okText="Yes"
                            cancelText="No"
                        >
                            <ArchiveRestore className="ArchiverestorImg" />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];
    

    return (
        <>
            <div className='sticyaction'>
                <Row className="pagenamerow" justify="space-between" align="middle">
                    <Col>

                        <h2>Archived Inquiry</h2>
                        <div className="bredcrumbwrp">
                            <Link to="/inquiry-list" className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/inquiry-list">Inquiry</Link> },
                                    { title: 'Archived Inquiry' }
                                ]}
                            />
                        </div>
                    </Col>
                    <Col>
                        <Space size="small">
                            <Input
                                placeholder="Search"
                                className='searchbox'
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={handleSearch}
                                style={{ width: '100%' }}
                            />
                            <Tooltip placement="top" title={'Reset Filter'}>
                                <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                                    <FilterIcon />
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </div>
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, overflow: 'auto' }}>
                    <Table
                       
                        dataSource={dataSource}
                        columns={columns}
                        scroll={{
                            x: 280, // Horizontal Scroll
                            y: 500,  // Vertical Scroll with Fixed Header
                        }}
                        loading={loading}
                        // pagination={{
                        //     defaultCurrent: page,
                        //     defaultPageSize: pagesize,
                        //     total: totalRecord,
                        //     showTotal: (total) => `Total ${total} items`,
                        //     showSizeChanger: true,
                        //     onChange: handlePageChange,
                        //     locale: {
                        //         items_per_page: 'Items / Page', // Text for the size changer
                        //     },
                        // }}
                        pagination={false}
                        onChange={handleTableChange}
                        style={{ marginTop: '20px' }}
                    />
                </div>
                <div
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1000,
                        background: '#fff',
                        padding: 10,
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Pagination
                        current={page}
                        pageSize={pagesize}
                        total={totalRecord}
                        showSizeChanger
                        onShowSizeChange={handlePageSizeChange}
                        showTotal={total => `Total ${total} items`}
                        onChange={handlePageChange}
                        locale={{ items_per_page: 'Items / Page' }}
                    />
                </div>
            </div>
        </>
    );
}

export default ArchiveInquiryList;
